/**
 * 用户标签管理 Zod（ADM-USR-S008，A3 §11）
 *
 * 四个端点均不接收 app_id；binding_status 只出现在 response。
 * §9.3 预置 tag_key 清单在本文件（用于辨识系统预置行；不可删 / 不可改 key 对所有行都成立）。
 */

import { z } from 'zod';

export const USER_TAG_KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

/**
 * `03-数据字典/10-用户.md` §9.3 预置行清单（migration seed，全库一行），**顺序与该表逐行一致**。
 * 不是 tag_key 值域闭集。
 *
 * 「不可删、不可改 tag_key」对**所有**行都成立（A3 §11：无 DELETE 端点；PATCH 的 strict schema 不收
 * `tag_key`），本清单不是那道保护的来源。它承担的是**可辨识**：这几行的语义被 C 端规则按 `tag_key`
 * 字面消费（§9.4 各作用面闭集），运营在后台必须能一眼认出它们是系统预置、不是某次运营活动建的普通标签
 * —— 认不出来，就可能「停用了再新建一个同义的」，而 C 端只认这一行。漏一行 = 该行在列表里与普通标签
 * 同形。新增 §9.3 行时同步本清单与 `user-tags.test.ts` 的逐行断言。
 */
export const PRESET_USER_TAG_KEYS = [
  'store_review_account',
  'integration_test_account',
  'internal_test_account',
] as const;

export type PresetUserTagKey = (typeof PRESET_USER_TAG_KEYS)[number];

const PRESET_USER_TAG_KEY_SET: ReadonlySet<string> = new Set(PRESET_USER_TAG_KEYS);

export function isPresetUserTagKey(tagKey: string): tagKey is PresetUserTagKey {
  return PRESET_USER_TAG_KEY_SET.has(tagKey);
}

export const CreateUserTagRequestSchema = z
  .object({
    tag_key: z.string().regex(USER_TAG_KEY_PATTERN),
    name: z.string().min(1).max(128),
    description: z.string().max(2000).optional(),
  })
  .strict();

export const UpdateUserTagRequestSchema = z
  .object({
    name: z.string().min(1).max(128).optional(),
    description: z.string().max(2000).nullable().optional(),
  })
  .strict()
  .refine((v) => v.name !== undefined || v.description !== undefined, {
    message: '至少提供 name 或 description',
  });

export const ToggleUserTagActiveRequestSchema = z
  .object({
    is_active: z.boolean(),
  })
  .strict();

export const UserTagBindingStatusSchema = z.enum(['bound', 'unbound']);

export const UserTagDtoSchema = z.object({
  id: z.string().uuid(),
  tag_key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  binding_status: UserTagBindingStatusSchema,
  is_active: z.boolean(),
  updated_by_name: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type CreateUserTagRequest = z.infer<typeof CreateUserTagRequestSchema>;
export type UpdateUserTagRequest = z.infer<typeof UpdateUserTagRequestSchema>;
export type ToggleUserTagActiveRequest = z.infer<typeof ToggleUserTagActiveRequestSchema>;
export type UserTagDto = z.infer<typeof UserTagDtoSchema>;
